/**
 * "Client report pages" — every campaign report page, with the advertiser
 * page each one is listed on and a filter by advertiser page. Row actions are
 * delegated to the tab (refresh data, extend expiry, edit, reset PIN,
 * revoke/restore, delete); revoke/restore and delete sit in the row's ⋯ menu.
 */

import React, { useMemo, useState } from 'react';
import { Button, EmptyState, PinCell, RowActionsMenu, Spinner, buttonStyles, tableStyles } from '../../ui';
import type { CampaignReportPage } from '../../../services/campaignReportsApi';
import type { AdvertiserReportPage } from '../../../services/advertiserReportsApi';
import {
  EXPIRY_DAYS_AFTER_FLIGHT_END,
  advertiserPageUrl,
  campaignPageUrl,
  campaignManualRefreshBlocker,
  fieldStyle,
  fmtDate,
  sectionSubtitleStyle,
  sectionTitleStyle,
} from './shared';
import { PageStatus } from './pills';

interface CampaignPagesTableProps {
  pages: CampaignReportPage[];
  loading: boolean;
  hubs: AdvertiserReportPage[];
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
  onRefreshData: (page: CampaignReportPage) => void;
  onExtendExpiry: (page: CampaignReportPage) => void;
  onEdit: (page: CampaignReportPage) => void;
  onResetPin: (page: CampaignReportPage) => void;
  onToggleRevoke: (page: CampaignReportPage) => void;
  onDelete: (page: CampaignReportPage) => void;
}

type HubFilter = 'all' | 'none' | string;

const hubChipStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '3px 9px',
  borderRadius: 'var(--radius-pill)',
  fontSize: 11.5,
  fontWeight: 600,
  background: 'var(--accent-soft)',
  color: 'var(--accent-soft-fg)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'middle',
};

/**
 * Accessible name for a row's controls. Campaign pages under one advertiser
 * usually share a client label (it defaults to the advertiser name), so the
 * order name keeps "Copy PIN for …" unique per row.
 */
const rowLabel = (p: CampaignReportPage): string =>
  p.gamOrderName && p.gamOrderName !== p.clientLabel ? `${p.clientLabel} (${p.gamOrderName})` : p.clientLabel;

const CampaignPagesTable: React.FC<CampaignPagesTableProps> = ({
  pages,
  loading,
  hubs,
  copiedKey,
  onCopy,
  onRefreshData,
  onExtendExpiry,
  onEdit,
  onResetPin,
  onToggleRevoke,
  onDelete,
}) => {
  const [hubFilter, setHubFilter] = useState<HubFilter>('all');

  const hubBySlug = useMemo(() => new Map(hubs.map((h) => [h.slug, h])), [hubs]);
  const hubLabel = (slug: string) => hubBySlug.get(slug)?.clientLabel ?? slug;
  const hubUrl = (slug: string) => hubBySlug.get(slug)?.url ?? advertiserPageUrl(slug);

  /** Advertiser pages referenced by at least one campaign page (the filter's options). */
  const referencedHubs = useMemo(() => {
    const labelOf = (slug: string) => hubBySlug.get(slug)?.clientLabel ?? slug;
    const slugs = new Set<string>();
    for (const p of pages) if (p.advertiserPageSlug) slugs.add(p.advertiserPageSlug);
    return Array.from(slugs).sort((a, b) => labelOf(a).localeCompare(labelOf(b)));
  }, [pages, hubBySlug]);

  const shown = useMemo(() => {
    if (hubFilter === 'all') return pages;
    if (hubFilter === 'none') return pages.filter((p) => !p.advertiserPageSlug);
    return pages.filter((p) => p.advertiserPageSlug === hubFilter);
  }, [pages, hubFilter]);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={sectionTitleStyle}>Client report pages</div>
          <div style={sectionSubtitleStyle}>
            Passcode-protected pages with hourly delivery updates that expire {EXPIRY_DAYS_AFTER_FLIGHT_END} days after the campaign ends.
          </div>
        </div>
        {referencedHubs.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--fg-3)' }}>
            Advertiser page
            <select
              style={{ ...fieldStyle, width: 'auto', padding: '6px 10px', fontSize: 12.5 }}
              value={hubFilter}
              onChange={(e) => setHubFilter(e.target.value)}
              aria-label="Filter by advertiser page"
            >
              <option value="all">All pages</option>
              <option value="none">Standalone (no advertiser page)</option>
              {referencedHubs.map((slug) => (
                <option key={slug} value={slug}>
                  {hubLabel(slug)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {loading && pages.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner />
        </div>
      ) : pages.length === 0 ? (
        <EmptyState title="No report pages yet" subtitle="Pick a campaign above to mint its client report page." />
      ) : shown.length === 0 ? (
        <div style={{ padding: '18px 14px', fontSize: 12.5, color: 'var(--fg-4)', textAlign: 'center' }}>
          No campaign pages match this filter.
        </div>
      ) : (
        <div style={tableStyles.wrapScroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Client / Campaign</th>
                <th style={tableStyles.th}>Advertiser page</th>
                <th style={tableStyles.th}>Reporting window</th>
                <th style={tableStyles.th}>PIN</th>
                <th style={tableStyles.th}>Expires</th>
                <th style={tableStyles.thNum}>Views</th>
                <th style={tableStyles.th}>Status</th>
                <th style={tableStyles.th} />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => {
                const label = rowLabel(p);
                return (
                  <tr key={p.slug}>
                    <td style={{ ...tableStyles.td, fontWeight: 600 }}>
                      {p.clientLabel}
                      <div style={{ fontSize: 11.5, color: 'var(--fg-4)', fontWeight: 400 }}>
                        {p.gamOrderName}
                        <br />/campaigns/{p.slug}/report
                      </div>
                    </td>
                    <td style={tableStyles.td}>
                      {p.advertiserPageSlug ? (
                        <a
                          href={hubUrl(p.advertiserPageSlug)}
                          target="_blank"
                          rel="noreferrer"
                          style={hubChipStyle}
                          title={`Listed on /advertisers/${p.advertiserPageSlug}/reports — the advertiser passcode also opens this report`}
                        >
                          {hubLabel(p.advertiserPageSlug)} ↗
                        </a>
                      ) : (
                        <span style={{ color: 'var(--fg-4)' }}>—</span>
                      )}
                      {p.createdVia === 'advertiser-sync' && (
                        <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 3 }}>created by advertiser sync</div>
                      )}
                    </td>
                    <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                      {fmtDate(p.reportStartDate)} → {fmtDate(p.requestedEndDate)}
                      <div style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>
                        {p.dataStatus === 'ready' ? <>data through {fmtDate(p.reportEndDate)}</> : <>&nbsp;</>}
                      </div>
                      {p.refreshHealth?.delivery?.lastSuccessAt && (
                        <div style={{ fontSize: 11.5, color: 'var(--fg-4)' }} title={p.refreshHealth.delivery.lastSuccessAt}>
                          Delivery updated {new Date(p.refreshHealth.delivery.lastSuccessAt).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} PT
                        </div>
                      )}
                    </td>
                    <td style={tableStyles.td}>
                      <PinCell
                        label={label}
                        pin={p.pin}
                        copied={copiedKey === `pin:${p.slug}`}
                        onCopy={() => onCopy(`pin:${p.slug}`, p.pin)}
                        onReset={() => onResetPin(p)}
                      />
                    </td>
                    <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {fmtDate(p.expiresAt)}
                        <Button size="sm" variant="ghost" onClick={() => onExtendExpiry(p)} title="Extend expiry by 30 days">
                          +30d
                        </Button>
                      </div>
                    </td>
                    <td style={tableStyles.tdNum}>{p.accessCount}</td>
                    <td style={tableStyles.td}>
                      <PageStatus page={p} />
                    </td>
                    <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                        <a
                          href={campaignPageUrl(p.slug)}
                          target="_blank"
                          rel="noreferrer"
                          style={buttonStyles('subtle', 'sm')}
                          aria-label={`Open ${label} (new tab)`}
                        >
                          Open ↗
                        </a>
                        <Button size="sm" variant="ghost" onClick={() => onCopy(`link:${p.slug}`, `${campaignPageUrl(p.slug)}\nPasscode: ${p.pin}`)}>
                          {copiedKey === `link:${p.slug}` ? 'Copied ✓' : 'Copy link + PIN'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!!campaignManualRefreshBlocker(p)}
                          onClick={() => onRefreshData(p)}
                          title={campaignManualRefreshBlocker(p) ?? 'Refresh from GAM using this report’s saved reporting window'}
                        >
                          Refresh from GAM
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onEdit(p)} aria-label={`Edit ${label}`}>
                          Edit
                        </Button>
                        <RowActionsMenu
                          label={label}
                          actions={[
                            { label: p.revokedAt ? 'Restore' : 'Revoke…', onSelect: () => onToggleRevoke(p) },
                            { label: 'Delete…', danger: true, onSelect: () => onDelete(p) },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default CampaignPagesTable;
